import type { Optional } from '@festina-life/core'
import { FolderIcon } from '@heroicons/react/24/outline'
import cx from 'classnames'
import {
  type ChangeEvent,
  type MouseEvent,
  type ReactNode,
  useMemo,
} from 'react'

import { Button } from '../../actions/button/Button'
import { IconButton } from '../../actions/iconButton/IconButton'
import { RawTextInput } from '../../forms/textInput/RawTextInput'
import { useTranslate } from '../../hooks/useTranslate'
import { getParentTreeNode } from './helpers'
import type { TreeLeafData, TreeNodeData } from './types'

type Props<Leaf extends TreeLeafData> = {
  trees: TreeNodeData<Leaf>[]
  title: string
  value: Optional<string[]>
  onChange: (nextPath: string[]) => void
  error: Optional<string>
  isDisabled?: boolean
  nameFromPathPart?: (part: string) => string
  renderNode?: (node: TreeNodeData<Leaf>) => ReactNode
}

export const PathSelector = <Leaf extends TreeLeafData>({
  trees,
  value,
  onChange,
  error,
  title,
  isDisabled,
  nameFromPathPart = (part) => part,
  renderNode,
}: Props<Leaf>) => {
  const translate = useTranslate()

  const isRootPath = !value?.length
  const currentPath = value?.map(nameFromPathPart).join('.')

  const pathParent = useMemo(
    () =>
      getParentTreeNode({
        tree: trees,
        path: value?.filter(Boolean),
        nameFromPathPart,
      }),
    [value, trees, nameFromPathPart],
  )
  const handleNavigateUpwardsClicked = (evt: MouseEvent<HTMLButtonElement>) => {
    evt.preventDefault()

    if (!value?.length) {
      throw new Error('Cannot navigate up from the root path')
    }

    onChange(value.slice(0, value.length - 1))
  }

  const handleNodeClicked =
    (node: TreeNodeData<Leaf>) => (evt: MouseEvent<HTMLButtonElement>) => {
      evt.preventDefault()

      onChange(node.path)
    }

  const handleCurrentPathChanged = (evt: ChangeEvent<HTMLInputElement>) => {
    const nextValue = evt.target.value.trim()
    const sanitisedValue = nextValue.replace(/\s/g, '')
    if (!sanitisedValue) {
      onChange([])
    } else {
      onChange(sanitisedValue.split('.'))
    }
  }

  const nodeSuggestions =
    value && value.length ? (pathParent?.nodes ?? []) : trees

  return (
    <div>
      <p className="flex flex-row justify-between">{title}</p>
      <div className="flex flex-col gap-y-2 py-2">
        <div className="flex flex-row items-center gap-x-2 text-lg font-medium">
          <RawTextInput
            addOnProps={{ element: '/' }}
            value={currentPath}
            onChange={handleCurrentPathChanged}
            disabled={isDisabled}
            isInError={!!error}
          />
        </div>
        {error && (
          <div className="mt-2 text-sm text-error wrap-break-word whitespace-pre-line w-full max-w-full overflow-hidden">
            {error}
          </div>
        )}
        {!isDisabled && (
          <div
            className={cx('grid grid-cols-[auto_1fr] gap-x-2', {
              'items-start': !!nodeSuggestions?.length,
              'items-center': !nodeSuggestions?.length,
            })}
          >
            <IconButton
              onClick={handleNavigateUpwardsClicked}
              className={cx(
                '!focus:ring-0 text-primary dark:text-primary-200 mt-2',
                {
                  'opacity-25 cursor-not-allowed': isRootPath,
                },
              )}
              disabled={isRootPath}
              tip={null}
              iconName="ArrowUp"
            />
            <div className="flex flex-col">
              {nodeSuggestions?.map((treeNode) => (
                <Button
                  variant="discreet"
                  className="flex flex-row items-center justify-start gap-x-2 py-1! px-0 text-amaze-ink! focus:ring-0! dark:text-amaze-inkBright!"
                  key={treeNode.name}
                  onClick={handleNodeClicked(treeNode)}
                >
                  {renderNode ? (
                    renderNode(treeNode)
                  ) : (
                    <>
                      <FolderIcon className="size-5" />
                      {treeNode.name}
                    </>
                  )}
                </Button>
              ))}
              <div className="mt-2 opacity-50">
                {translate('count.items', {
                  count: pathParent?.leaves.length ?? 0,
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
